import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { useState } from "preact/hooks"
import useSWR from "swr"
import { BookOpen, CheckCircle, Loader2 } from "lucide-react"
import { format } from "date-fns"
import {
  CheckJournalEntryJson,
  getCheckJournalEntry,
  postCheckJournalEntry,
} from "@/api/accounting.tsx"

interface MonthEndJournalEntryControlProps {
  checkId: string
  className?: string
}

/**
 * Month-end journal entry action for a check whose cash was already applied externally (a synced
 * accounting-system credit memo exists). Posts the deduction GL detail as a NetSuite journal entry:
 * debit the coded deduction accounts, credit the configured clearing account. Shown only for
 * NetSuite orgs — the server rejects other providers.
 */
export function MonthEndJournalEntryControl({ checkId, className }: MonthEndJournalEntryControlProps) {
  const [popover_open, set_popover_open] = useState(false)
  const [posting_date, set_posting_date] = useState<string>(format(new Date(), "yyyy-MM-dd"))
  const [is_posting, set_is_posting] = useState(false)

  const { data: journal_entry, mutate } = useSWR(
    ["check-journal-entry", checkId],
    async (): Promise<CheckJournalEntryJson | null> => {
      const result = await getCheckJournalEntry(checkId)
      return result.ok ? result.value.data : null
    }
  )

  async function handle_post() {
    set_is_posting(true)
    const result = await postCheckJournalEntry({ check_id: checkId, posting_date })
    set_is_posting(false)

    if (result.ok && result.value.data.published_at) {
      toast({ title: "Journal entry posted to NetSuite" })
      set_popover_open(false)
      await mutate(result.value.data)
    } else {
      const message = result.ok
        ? result.value.data.error || "Unknown error"
        : result.error.message || "Failed to post journal entry"
      toast({ title: "Failed to post journal entry", description: message, variant: "destructive" })
      await mutate()
    }
  }

  if (journal_entry?.published_at) {
    return (
      <Button variant="outline" class={`gap-2 text-green-700 border-green-200 ${className ?? ""}`}>
        <CheckCircle className="h-4 w-4" />
        Month-end JE posted
      </Button>
    )
  }

  return (
    <Popover open={popover_open} onOpenChange={set_popover_open}>
      <PopoverTrigger asChild>
        <Button variant="outline" class={`gap-2 ${className ?? ""}`}>
          <BookOpen className="h-4 w-4" />
          Post Month-End JE
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-80">
        <div class="space-y-3">
          <div class="font-medium">Post month-end journal entry</div>
          <p class="text-sm text-gray-600">
            Cash for this check was already applied in NetSuite. This posts the deduction GL detail as a
            journal entry: debit the coded deduction accounts, credit the holding account.
          </p>
          <div class="space-y-1.5">
            <Label htmlFor="je-posting-date">Posting date</Label>
            <Input
              id="je-posting-date"
              type="date"
              value={posting_date}
              onInput={(e: Event) => set_posting_date((e.target as HTMLInputElement).value)}
            />
          </div>
          {journal_entry?.error ? (
            <p class="text-sm text-red-600 break-words">Last attempt failed: {journal_entry.error}</p>
          ) : null}
          <div class="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => set_popover_open(false)}>
              Cancel
            </Button>
            <Button size="sm" class="bg-green-600 hover:bg-green-700" disabled={is_posting} onClick={() => void handle_post()}>
              {is_posting ? (
                <span class="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Posting…
                </span>
              ) : (
                "Post to NetSuite"
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
